import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 2: "Linear Search" (Master Spec Section 5), campaign
 * levels 11-20. Teaches: sequential checking, start-to-end traversal, comparisons, early termination, index, basic efficiency observation.
 * Algorithm(s) used in this world: linear-search.
 */
export const world02LinearSearchLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-011",
    world: 2,
    levelInWorld: 1,
    globalLevelNumber: 11,
    algorithm: "linear-search",
    values: [8, 15, 3, 29, 41, 17, 24],
    target: 8,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Name this algorithm: Linear Search -- checking items in order from the start.",
      "See early termination: the search stops the instant a match is found, at index 0.",
    ],
    notes: "Target at index 0 -- Linear Search can stop after just one comparison.",
  }),
  createCampaignLevel({
    id: "campaign-012",
    world: 2,
    levelInWorld: 2,
    globalLevelNumber: 12,
    algorithm: "linear-search",
    values: [8, 15, 3, 29, 41, 17, 24],
    target: 3,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Track the exact index where the match occurs as the search progresses.",
      "See two non-matching comparisons before the match.",
    ],
    notes: "Target at index 2 -- three comparisons total including the match.",
  }),
  createCampaignLevel({
    id: "campaign-013",
    world: 2,
    levelInWorld: 3,
    globalLevelNumber: 13,
    algorithm: "linear-search",
    values: [8, 15, 3, 29, 41, 17, 24],
    target: 29,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Practice reading comparisons-so-far as a running count, not just a final result.",
      "Reinforce start-to-end traversal: index 0, 1, 2 and onward in strict order.",
    ],
    notes: "Target sits at the midpoint index of this 7-item dataset.",
  }),
  createCampaignLevel({
    id: "campaign-014",
    world: 2,
    levelInWorld: 4,
    globalLevelNumber: 14,
    algorithm: "linear-search",
    values: [8, 15, 3, 29, 41, 17, 24],
    target: 17,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "See early termination still apply even when many comparisons happen first.",
      "Build a sense of how index position drives comparison count.",
    ],
    notes: "Target at index 5 -- five non-matching comparisons before the match.",
  }),
  createCampaignLevel({
    id: "campaign-015",
    world: 2,
    levelInWorld: 5,
    globalLevelNumber: 15,
    algorithm: "linear-search",
    values: [8, 15, 3, 29, 41, 17, 24],
    target: 24,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "See the worst case for a found search: every other item is checked first.",
      "Contrast index 6 here with index 0 in level 11 -- same dataset, opposite cost.",
    ],
    notes: "Target is the last item -- six comparisons before the match, the seventh finds it.",
  }),
  createCampaignLevel({
    id: "campaign-016",
    world: 2,
    levelInWorld: 6,
    globalLevelNumber: 16,
    algorithm: "linear-search",
    values: [8, 15, 3, 29, 41, 17, 24],
    target: 50,
    targetPresent: false,
    isSorted: false,
    learningObjectives: [
      "See Linear Search exhaust an entire dataset with no early termination possible.",
      "Understand not found always costs the maximum number of comparisons.",
    ],
    notes: "50 is absent -- all 7 items are checked with no match, unlike levels 11-15's early stops.",
  }),
  createCampaignLevel({
    id: "campaign-017",
    world: 2,
    levelInWorld: 7,
    globalLevelNumber: 17,
    algorithm: "linear-search",
    values: [12, 47, 3, 58, 21, 39, 64, 8, 33],
    target: 12,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Observe how a larger dataset does not change a beginning match's cost.",
      "Set up a direct efficiency comparison against level 18's ending match.",
    ],
    notes: "9-item dataset, target at index 0 -- only one comparison needed, same as a small dataset would take.",
  }),
  createCampaignLevel({
    id: "campaign-018",
    world: 2,
    levelInWorld: 8,
    globalLevelNumber: 18,
    algorithm: "linear-search",
    values: [12, 47, 3, 58, 21, 39, 64, 8, 33],
    target: 33,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "See how a larger dataset makes an ending match noticeably more expensive.",
      "Compare directly against level 17: same dataset, opposite position, very different comparison counts.",
    ],
    notes: "Same 9-item dataset as level 17, target at the last index -- nine comparisons instead of one.",
  }),
  createCampaignLevel({
    id: "campaign-019",
    world: 2,
    levelInWorld: 9,
    globalLevelNumber: 19,
    algorithm: "linear-search",
    values: [12, 47, 3, 58, 21, 39, 64, 8, 33],
    target: 99,
    targetPresent: false,
    isSorted: false,
    learningObjectives: [
      "See a full not found search on the largest dataset in this world.",
      "Reinforce that dataset size directly increases the cost of proving absence.",
    ],
    notes: "99 is absent from all 9 items -- the most expensive possible outcome for this dataset.",
  }),
  createCampaignLevel({
    id: "campaign-020",
    world: 2,
    levelInWorld: 10,
    globalLevelNumber: 20,
    algorithm: "linear-search",
    values: [16, 5, 38, 22, 9, 45, 13, 27],
    target: 22,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Consolidate Linear Search: sequential order, comparisons, early termination, and index.",
      "Predict the comparison count before running the search, then check the result.",
    ],
    notes: "World 2 capstone -- an 8-item dataset with the target in the middle, reviewing everything this world taught.",
  }),
];
